import { useRef, useEffect } from 'react';

/**
 * useSeenCutoff — "what's new since your last visit" for a nav page.
 *
 * The sidebar shows a numeric activity badge per page (see useSidebarBadges),
 * but when you open the page nothing tells you WHICH items the badge counted.
 * This hook closes that loop: it remembers, per nav id, the timestamp of your
 * last visit (persisted in localStorage), and hands the page a frozen `cutoff`.
 * Anything newer than the cutoff is "new" — the page highlights it and can show
 * an "N new since your last visit" indicator, so the badge number finally has a
 * visible meaning.
 *
 * Contract:
 *   - `cutoff` is read ONCE at mount and frozen for the whole visit, so items
 *     that stream in while you're looking stay highlighted (they're new to you).
 *   - "seen up to now" is written when you LEAVE the page (unmount / route
 *     change). If the tab is hard-closed instead, the cutoff simply stays older
 *     and the next visit over-highlights rather than missing anything.
 *
 * @param {string} navId  sidebar nav id, e.g. 'sacred-timeline' | 'chat' | 'dossier'
 * @returns {number} cutoff timestamp (ms). Items with ts > cutoff are "new".
 */
const STORAGE_KEY = 'navLastSeen';

function readMap() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; }
    catch { return {}; }
}

function writeMap(map) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(map)); }
    catch { /* storage full / unavailable — highlighting is best-effort */ }
}

/**
 * Same-tab subscribers to cutoff changes. localStorage's `storage` event only
 * fires in OTHER tabs, so the sidebar badge — which counts items against these
 * cutoffs — needs an in-tab signal to recompute the moment a page advances its
 * last-seen (e.g. when you leave the Behavioral Profile page). This is what
 * keeps the badge and the page's "N new" indicator in lockstep: one cutoff,
 * one notification, both read it.
 */
const seenSubscribers = new Set();

/** Subscribe to last-seen changes. cb(navId, ts). Returns an unsubscribe fn. */
export function subscribeSeen(cb) {
    seenSubscribers.add(cb);
    return () => seenSubscribers.delete(cb);
}

/** Last-visit timestamp for a nav id (0 if never visited). */
export function getLastSeen(navId) {
    if (!navId) return 0;
    const v = readMap()[navId];
    return typeof v === 'number' ? v : 0;
}

/** Record that everything up to `ts` has been seen for this nav id. */
export function markSeen(navId, ts = Date.now()) {
    if (!navId) return;
    const map = readMap();
    map[navId] = ts;
    writeMap(map);
    for (const cb of seenSubscribers) {
        try { cb(navId, ts); } catch { /* a bad subscriber shouldn't break others */ }
    }
}

export function useSeenCutoff(navId) {
    // Freeze the previous last-seen exactly once, before this visit advances it.
    const cutoffRef = useRef(null);
    if (cutoffRef.current === null) cutoffRef.current = getLastSeen(navId);

    useEffect(() => {
        // On leave, everything shown this visit is now "seen".
        return () => { markSeen(navId, Date.now()); };
    }, [navId]);

    return cutoffRef.current;
}
